import * as React from "react";

import { FormResources } from "../../../Forms/FormResources";
import { afterFormInit, initForm, initialLoadInner } from "../../../Forms/InitForm";
import { IForm } from "../../../Engine/Form/IForm";
import { useAsyncEffect } from "../../../../../Common/UseAsyncEffect";
import { reject, stringifyUnknownError } from "../../../../../Common/TypingUtils";
import { NormalizationProgressTask } from "../../../../../Common/CandyApiClient/ICandyApiClient";
import { delay } from "../../../../../Common/AsyncUtils";
import { prepareLazyLoadingManager } from "../../../Forms/FormInitialization/DataFetching";
import { NeedNormalizationError } from "../../../../../Common/CandyApiClient/Types/DraftActions/NeedNormalizationError";

const NORMALIZATION_PROGRESS_POLLING_DELAY = 2000;

interface InitFormHookResult {
    form: IForm | undefined;
    normalizationProgress: NormalizationProgressTask | undefined;
    formInitError: undefined | Error;
}
export const useInitForm = (formResources: FormResources): InitFormHookResult => {
    const [form, setForm] = React.useState<IForm | undefined>(undefined);
    const [error, setError] = React.useState<Error | undefined>(undefined);
    const [normalizationProgress, setNormalizationProgress] = React.useState<NormalizationProgressTask | undefined>(
        undefined
    );

    const getOrPoll = async <T>(func: () => Promise<T>, createdForm: IForm): Promise<T> => {
        try {
            const data = await func();
            return data;
        } catch (e) {
            if (e instanceof NeedNormalizationError) {
                let normalizationTask = await createdForm.candyApiDraftClient.startNormalizationTask();
                setNormalizationProgress(normalizationTask);

                while (normalizationTask?.status !== "Success") {
                    if (normalizationTask?.status === "Error") {
                        reject(`Task ${normalizationTask?.id} was finished unsuccessfully`);
                    }
                    await delay(NORMALIZATION_PROGRESS_POLLING_DELAY);
                    normalizationTask = await createdForm.candyApiDraftClient.getNormalizationTask();
                    setNormalizationProgress(normalizationTask);
                }

                return func();
            }

            throw e;
        }
    };

    useAsyncEffect(async () => {
        try {
            const createdForm = await initForm(formResources);
            await getOrPoll(
                () => prepareLazyLoadingManager(createdForm, createdForm.settings.useServerSideFLangNormalization),
                createdForm
            );
            await getOrPoll(() => initialLoadInner(createdForm), createdForm);
            await afterFormInit(createdForm);
            setForm(createdForm);
        } catch (e) {
            if (e instanceof Error) {
                setError(e);
            } else {
                setError(new Error(stringifyUnknownError(`${e}`)));
            }
        }
    }, []);

    return { form: form, normalizationProgress: normalizationProgress, formInitError: error };
};
